import { RefObject, useEffect, useRef, useState } from 'react';

import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import PenIcon from '@cartwhl/ui/icons/PenIcon';

import { projectStore } from 'stores';
import { LEGACY_PROJECT_ID } from 'stores/projectStore';

export default function EditableProjectName({
  projectID,
  projectName,
  ref,
}: {
  projectID: string;
  projectName: string;
  ref?: RefObject<HTMLDivElement>;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(projectName);
  const isLegacyProject = projectID === LEGACY_PROJECT_ID;

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setName(projectName);
  }, [projectName]);

  const handleClick = () => {
    if (isLegacyProject) return;
    setIsEditing(true);
  };

  const handleBlur = async () => {
    setIsEditing(false);
    if (name.trim() !== '') {
      await projectStore.updateProjectName(projectID, name);
      setName(projectName);
    } else {
      setName(projectName);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleBlur();
    }
  };

  useEffect(() => {
    if (isEditing && inputRef.current && !inputRef.current.matches(':focus')) {
      inputRef.current.focus();
      inputRef.current.setSelectionRange(0, inputRef.current.value.length);
    }
  }, [isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      const input = inputRef.current;
      const measure = document.createElement('span');
      measure.style.visibility = 'hidden';
      measure.style.position = 'absolute';
      measure.style.whiteSpace = 'pre';
      measure.style.font = window.getComputedStyle(input).font;
      measure.textContent = input.value || input.placeholder || 'M';
      document.body.appendChild(measure);
      const width = measure.offsetWidth;
      document.body.removeChild(measure);
      input.style.width = `${Math.max(width + 10, input.offsetWidth)}px`;
    }
  }, [isEditing, name]);

  return (
    <div ref={ref} className="flex min-w-0 flex-1 items-center">
      {isEditing ? (
        <div className="min-w-0 flex-1 overflow-x-auto">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            ref={inputRef}
            className="w-full border-none bg-transparent text-left font-sans text-[16px]/[20px] font-normal outline-hidden"
            style={{ width: 'max-content', minWidth: '100%' }}
          />
        </div>
      ) : isLegacyProject ? (
        <Text variant="studio/body/md" className="truncate">
          {name}
        </Text>
      ) : (
        <Tooltip text="Rename project">
          <button
            onClick={handleClick}
            className="group flex min-w-0 flex-1 items-center gap-1"
          >
            <Text variant="studio/body/md" className="truncate">
              {name}
            </Text>
            <PenIcon className="text-gray-60 group-hover:border-gray-30 dark:group-hover:border-gray-70 hover:bg-gray-30 dark:hover:bg-gray-70 -my-0.5 h-6 w-6 flex-shrink-0 cursor-pointer rounded-lg border border-transparent p-0.5 transition-colors" />
          </button>
        </Tooltip>
      )}
    </div>
  );
}
